import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Wrench, Phone, Mail, AlertTriangle } from "lucide-react";
import { PageHeader } from "./fordon";
import { QuickPartContact } from "@/components/QuickPartContact";
import { RelatedLinks } from "@/components/RelatedLinks";
import { clampDescription } from "@/lib/seo-quality";
import { getPartsBrand, PARTS_BRANDS } from "@/data/reservdelar-brands";
import { PARTS_CATEGORIES } from "@/data/reservdelar-kategorier";

export const Route = createFileRoute("/reservdelar_/$brand")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const brand = getPartsBrand(params.brand);
    if (!brand) throw notFound();
    return { brand };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const b = loaderData.brand;
    const url = `https://www.akhusbilfritid.se/reservdelar/${b.slug}`;
    const title = buildTitle(`${b.name} reservdelar – pris och leverans 24 h`);
    const desc = clampDescription(
      `${b.name} reservdelar till ${b.type === "båda" ? "husbil och husvagn" : b.type}: ${b.parts
        .slice(0, 3)
        .map((p) => p.part.toLowerCase())
        .join(", ")}. Skicka bild och chassinummer – pris inom 24 h.`,
    );
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: b.keywords },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: b.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: "Reservdelar", item: "https://www.akhusbilfritid.se/reservdelar" },
              { "@type": "ListItem", position: 3, name: `${b.name} reservdelar`, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BrandParts,
});

function BrandParts() {
  const { brand: b } = Route.useLoaderData();
  const cats = PARTS_CATEGORIES.filter((c) => c.brandSlugs.includes(b.slug));

  return (
    <main className="pb-20">
      <PageHeader
        label="Reservdelar"
        title={`${b.name} reservdelar`}
        sub={b.tagline}
      />

      <section className="container-edge">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-3xl border border-accent/30 bg-accent/5 p-6">
            <p className="font-display text-lg">Kort svar</p>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
              <li>
                <strong className="text-foreground">Detta behöver vi:</strong> bild på delen,
                chassinummer och årsmodell. Artikelnummer behövs inte.
              </li>
              <li>
                <strong className="text-foreground">Svarstid:</strong> pris och leveranstid inom 24 h
                på vardagar.
              </li>
              <li>
                <strong className="text-foreground">Leverans:</strong> lagerförda delar 1–3
                arbetsdagar, fabriksbeställda karossdelar 1–4 veckor.
              </li>
              <li>
                <strong className="text-foreground">Montering:</strong> egen verkstad i Kalix, annars
                del + monteringsanvisning och telefonhjälp.
              </li>
            </ul>
          </div>
          <QuickPartContact label={b.name} />

          <p className="mt-8 text-lg leading-relaxed text-muted-foreground">{b.intro}</p>


          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/reservdelar"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              <Mail className="h-4 w-4" /> Skicka delförfrågan
            </Link>
            <a
              href="tel:+46923777100"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium transition hover:bg-secondary"
            >
              <Phone className="h-4 w-4" /> Ring verkstaden
            </a>
          </div>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">
            Så identifierar du rätt {b.name}-del
          </h2>
          <p className="mt-4 leading-relaxed text-muted-foreground">{b.idPlate}</p>
          <p className="mt-4 text-sm text-muted-foreground">
            Tekniken i {b.name} kommer från {b.suppliers.join(", ")} — vilken leverantör du har avgör
            vilken del som passar. Läs mer i vår{" "}
            <Link
              to="/blogg/$slug"
              params={{ slug: "reservdelar-husbil-chassinummer-arsmodell-guide" }}
              className="underline decoration-accent/60 underline-offset-4"
            >
              guide om chassinummer och årsmodell
            </Link>
            .
          </p>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">
            Vanliga {b.name}-delar och prisspann
          </h2>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Del</th>
                  <th className="px-4 py-3">Detta behöver vi veta</th>
                  <th className="px-4 py-3 whitespace-nowrap">Prisspann</th>
                </tr>
              </thead>
              <tbody>
                {b.parts.map((p) => (
                  <tr key={p.part} className="border-t border-border align-top">
                    <td className="px-4 py-3 font-medium">{p.part}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.detail}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Priser är ungefärliga inklusive moms, exklusive montering, och varierar med årsmodell och
            leverantör.
          </p>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">
            Kända svagheter på {b.name}
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {b.weakSpots.map((w) => (
              <div key={w.title} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-accent">
                  <AlertTriangle className="h-4 w-4" />
                  <p className="font-medium text-sm text-foreground">{w.title}</p>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
              </div>
            ))}
          </div>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">Frågor & svar</h2>
          <div className="mt-6 space-y-4">
            {b.faq.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                <p className="font-medium">{f.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>

          {cats.length > 0 && (
            <div className="mt-14 rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-lg">{b.name}-delar per kategori</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {cats.map((x) => (
                  <Link
                    key={x.slug}
                    to="/reservdelar/kategori/$slug"
                    params={{ slug: x.slug }}
                    className="rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:bg-secondary"
                  >
                    {x.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 rounded-3xl border border-border bg-secondary/40 p-6">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-accent" />
              <p className="font-display text-lg">Delar till andra märken</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PARTS_BRANDS.filter((x) => x.slug !== b.slug).map((x) => (
                <Link
                  key={x.slug}
                  to="/reservdelar/$brand"
                  params={{ brand: x.slug }}
                  className="rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:bg-secondary"
                >
                  {x.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <RelatedLinks
              keyId={`reservdelar-${b.slug}`}
              text={`${b.name} reservdel del verkstad service fukt kylskåp gasol vatten broms fönster`}
              currentHref={`/reservdelar/${b.slug}`}
              max={6}
            />
          </div>
        </div>
      </section>
    </main>
  );
}
